import TreeItem from '@/storybook/components/FormCollection/TreeSelect/Components/TreeItem/TreeItem';
import DropdownDivider from '@/storybook/components/FormCollection/components/DropdownDivider/DropdownDivider';
import ListHeader from '@/storybook/components/FormCollection/components/ListHeader/ListHeader';
import { useFloatingLabelVirtualizedOptions } from '@/storybook/components/FormCollection/hooks/useFloatingLabelVirtualizedOptions';
import { VirtualRowMeta } from '@/storybook/components/FormCollection/hooks/useVirtualizedOptions';
import { getCascadingItemBadge } from '@/storybook/components/FormCollection/utils/cascadingItemBadge';
import { EMPTY_CATEGORY_KEYS, getCategoryKey } from '@/storybook/components/FormCollection/utils/categoryOptions';
import {
  createSelectItemKey,
  extractSelectItemLabelText,
  getPointerModifiersFromSyntheticEvent,
  getSelectItemLabelContent,
} from '@/storybook/components/FormCollection/utils/utils';
import { SelectItem } from '@/types/ui';
import cx from 'classnames';
import { useCallback, useMemo, type ChangeEvent, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../../MultiSelectFloatingLabel.module.css';
import type { MultiSelectOptionClickModifiers } from './useMultiSelectHandlersFloatingLabel';

type UseMultiSelectVirtualizationProps<T> = {
  selectedOptions?: SelectItem<T>[];
  notSelectedOptions?: SelectItem<T>[];
  categoriesToDisplay: { category: string; index: number }[];
  noCategoryLabel: string;
  handleOptionClick: (
    clickedValue: T,
    currentScopedOptions: T[],
    index: number,
    modifiers: MultiSelectOptionClickModifiers,
  ) => void;
  isSelected: (value: T) => boolean;
  collapsibleCategories?: boolean;
  isCategoryCollapsed?: (categoryKey: string) => boolean;
  onToggleCategory?: (categoryKey: string) => void;
  insetOptionRows?: boolean;
};

type MultiSelectVirtualizedOptionRowProps<T> = {
  option: SelectItem<T>;
  isSelectedRow: boolean;
  optionValues: T[];
  indexWithinGroup: number;
  handleOptionClick: UseMultiSelectVirtualizationProps<T>['handleOptionClick'];
  isSelected: (value: T) => boolean;
  disabledTooltip: string;
  isInset: boolean;
};

const MultiSelectVirtualizedOptionRow = <T,>({
  option,
  isSelectedRow,
  optionValues,
  indexWithinGroup,
  handleOptionClick,
  isSelected,
  disabledTooltip,
  isInset,
}: MultiSelectVirtualizedOptionRowProps<T>) => {
  const { labelText, labelContent, optionKey } = useMemo(
    () => ({
      labelText: extractSelectItemLabelText(option),
      labelContent: getSelectItemLabelContent(option),
      optionKey: createSelectItemKey(option),
    }),
    [option],
  );

  const rowPrefix = isSelectedRow ? 'sortedSelectedOption' : 'sortedNotSelectedOption';
  const checkboxTestId = useMemo(() => `${rowPrefix}-${optionKey}`, [optionKey, rowPrefix]);
  const isDisabled = option.disabled === true;

  const handleRowClick = useCallback(
    (e: SyntheticEvent<HTMLDivElement>) => {
      handleOptionClick(option.value, optionValues, indexWithinGroup, getPointerModifiersFromSyntheticEvent(e));
    },
    [handleOptionClick, indexWithinGroup, option.value, optionValues],
  );

  const handleCheck = useCallback(
    (e?: ChangeEvent<HTMLInputElement>) => {
      handleOptionClick(option.value, optionValues, indexWithinGroup, getPointerModifiersFromSyntheticEvent(e));
    },
    [handleOptionClick, indexWithinGroup, option.value, optionValues],
  );

  const checked = isSelected(option.value);
  const badge = getCascadingItemBadge(option, checked);

  return (
    <TreeItem
      className={cx({ [styles.noSelect]: !isSelectedRow, [styles.insetOptionRow]: isInset })}
      label={labelText}
      labelContent={labelContent}
      tooltipText={isDisabled ? disabledTooltip : undefined}
      forceTooltip={isDisabled && checked}
      variant="flatList"
      level={0}
      showCheckbox
      checked={checked}
      onCheck={isDisabled ? undefined : handleCheck}
      onClick={isDisabled ? undefined : handleRowClick}
      isDisabled={isDisabled}
      badge={badge}
      data-testid={`${rowPrefix}Row-${optionKey}`}
      checkboxDataTestId={checkboxTestId}
    />
  );
};

export const useMultiSelectVirtualizationFloatingLabel = <T,>({
  selectedOptions,
  notSelectedOptions,
  categoriesToDisplay,
  noCategoryLabel,
  handleOptionClick,
  isSelected,
  collapsibleCategories = false,
  isCategoryCollapsed,
  onToggleCategory,
  insetOptionRows = false,
}: UseMultiSelectVirtualizationProps<T>) => {
  const { t } = useTranslation();
  const disabledTooltip = t('components.dropdownPanel.valueNoLongerApplicable');

  const {
    virtualizedList,
    parentRef,
    selectedOptions: resolvedSelected,
    notSelectedOptions: resolvedNotSelected,
  } = useFloatingLabelVirtualizedOptions<T>({
    selectedOptions,
    notSelectedOptions,
    categoriesToDisplay,
    noCategoryLabel,
    isCategoryCollapsed,
  });

  const selectedOptionValues = useMemo(() => resolvedSelected.map(option => option.value), [resolvedSelected]);
  // Only visible rows, so shift-click range selection never reaches into a collapsed category.
  const notSelectedOptionValues = useMemo(
    () =>
      virtualizedList.virtualRowsData.flatMap(rowMeta =>
        rowMeta.kind === 'notSelected' && rowMeta.option ? [rowMeta.option.value] : [],
      ),
    [virtualizedList.virtualRowsData],
  );

  const selectedCategoryKeys = useMemo(() => {
    if (!collapsibleCategories) return EMPTY_CATEGORY_KEYS;
    return new Set(resolvedNotSelected.filter(option => isSelected(option.value)).map(getCategoryKey));
  }, [collapsibleCategories, resolvedNotSelected, isSelected]);

  const renderVirtualRow = useCallback(
    (vMeta: VirtualRowMeta<T>, virtualIndex?: number) => {
      if (vMeta.kind === 'divider') {
        return <DropdownDivider key={`divider-${virtualIndex}`} data-testid="multiselect-divider" />;
      }
      if (vMeta.kind === 'category' && vMeta.category) {
        const { categoryKey, isCollapsed = false } = vMeta;
        const handleToggle = categoryKey ? () => onToggleCategory?.(categoryKey) : undefined;
        return (
          <ListHeader
            key={`category-${vMeta.category}`}
            category={vMeta.category}
            isNoCategory={vMeta.isNoCategory}
            hideDivider={vMeta.isFirstCategory}
            collapsible={collapsibleCategories}
            isCollapsed={isCollapsed}
            isSelected={isCollapsed && !!categoryKey && selectedCategoryKeys.has(categoryKey)}
            onToggle={handleToggle}
          />
        );
      }
      if (vMeta.option) {
        const { option, indexWithinGroup = 0 } = vMeta;
        if (option == null) {
          return null;
        }
        const isSelectedRow = vMeta.kind === 'selected';
        const optionValues = isSelectedRow ? selectedOptionValues : notSelectedOptionValues;
        return (
          <MultiSelectVirtualizedOptionRow<T>
            option={option}
            isSelectedRow={isSelectedRow}
            optionValues={optionValues}
            indexWithinGroup={indexWithinGroup}
            handleOptionClick={handleOptionClick}
            isSelected={isSelected}
            disabledTooltip={disabledTooltip}
            isInset={insetOptionRows}
          />
        );
      }
      return null;
    },
    [
      handleOptionClick,
      selectedOptionValues,
      notSelectedOptionValues,
      isSelected,
      disabledTooltip,
      collapsibleCategories,
      selectedCategoryKeys,
      onToggleCategory,
      insetOptionRows,
    ],
  );

  return {
    virtualizedList,
    parentRef,
    renderVirtualRow,
  };
};
